import { NetworkStatus, useQuery } from "@apollo/client";
import isEqual from "lodash-es/isEqual";
import { useCallback, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";

import FormLoading from "components/form/components/loading";
import TileCheckboxGrid from "components/TileCheckboxGrid";
import Badge from "ds/components/Badge";
import Box from "ds/components/Box";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import IconEmphasized from "ds/components/IconEmphasized";
import Tile from "ds/components/Tile";
import TileTitle from "ds/components/Tile/Title";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";

import NewStackFooter from "../Footer";
import { INITIAL_FORM_DATA } from "../constants";
import { StackFormContext } from "../context";
import { StackCreationVendor, StackCreationWizardStep, StackVendorFormFields } from "../types";
import useErrorHandlerNewStack from "../useErrorHandlerNewStack";
import NewStackVendorAnsible from "./Ansible";
import NewStackVendorCloudFormation from "./CloudFormation";
import NewStackVendorDocumentation from "./Documentation";
import NewStackVendorKubernetes from "./Kubernetes";
import NewStackVendorOpenTofu from "./OpenTofu";
import NewStackVendorPulumi from "./Pulumi";
import NewStackVendorTerraform from "./Terraform";
import NewStackVendorTerragrunt from "./Terragrunt";
import { vendorConfig, vendorConfigWithOpenTofu } from "./constants";
import { NEW_STACK_RESOURCES } from "./gql";
import { NewStackResources } from "./types";

const INITIAL_VENDOR_DATA = INITIAL_FORM_DATA[StackCreationWizardStep.Vendor];

const NewStackVendor = () => {
  const { opentofuBackend } = useTypedFlags();
  const { currentStep, updateStepData, formData, asyncValidationLoading } =
    useTypedContext(StackFormContext);
  const stepData = formData[StackCreationWizardStep.Vendor];
  const vendorTiles = opentofuBackend ? vendorConfigWithOpenTofu : vendorConfig;

  const builderForm = useForm<StackVendorFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });

  const { control, setValue, getValues, watch } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const { data, loading, error, refetch, networkStatus } =
    useQuery<NewStackResources>(NEW_STACK_RESOURCES);

  useEffect(() => {
    if (data?.terraformVersions) {
      const formValues = getValues();

      if (!formValues.terraform.version.value) {
        setValue("terraform.version.value", data.openTofuVersions[0]);
      }

      if (!formValues.opentofu.version.value) {
        setValue("opentofu.version.value", data.openTofuVersions[0]);
      }

      if (!formValues.kubernetes.kubectlVersion) {
        setValue("kubernetes.kubectlVersion", data.kubectlVersions[0]);
      }

      if (!formValues.terragrunt.workflowVersion.value) {
        setValue("terragrunt.workflowVersion.value", data.openTofuVersions[0]);
      }

      if (!formValues.terragrunt.terragruntVersion.value) {
        setValue("terragrunt.terragruntVersion.value", data.terragruntVersions[0]);
      }
    }
  }, [data, getValues, setValue]);

  useErrorHandlerNewStack(error);

  const resetVendorValues = (vendor: StackCreationVendor) => {
    switch (vendor) {
      case StackCreationVendor.Terraform: {
        return setValue("terraform", {
          ...INITIAL_VENDOR_DATA.terraform,
          version: {
            ...INITIAL_VENDOR_DATA.terraform.version,
            value: data?.openTofuVersions[0] || "",
          },
        });
      }

      case StackCreationVendor.OpenTofu: {
        return setValue("opentofu", {
          ...INITIAL_VENDOR_DATA.opentofu,
          version: {
            ...INITIAL_VENDOR_DATA.opentofu.version,
            value: data?.openTofuVersions[0] || "",
          },
        });
      }

      case StackCreationVendor.Kubernetes: {
        return setValue("kubernetes", {
          ...INITIAL_VENDOR_DATA.kubernetes,
          kubectlVersion: data?.kubectlVersions[0] || "",
        });
      }

      case StackCreationVendor.Terragrunt: {
        return setValue("terragrunt", {
          ...INITIAL_VENDOR_DATA.terragrunt,
          workflowVersion: {
            ...INITIAL_VENDOR_DATA.terragrunt.workflowVersion,
            value: data?.openTofuVersions[0] || "",
          },
          terragruntVersion: {
            ...INITIAL_VENDOR_DATA.terragrunt.terragruntVersion,
            value: data?.terragruntVersions[0] || "",
          },
        });
      }

      case StackCreationVendor.Ansible: {
        return setValue("ansible", INITIAL_VENDOR_DATA.ansible);
      }

      case StackCreationVendor.CloudFormation: {
        return setValue("cloudFormation", INITIAL_VENDOR_DATA.cloudFormation);
      }

      case StackCreationVendor.Pulumi: {
        return setValue("pulumi", INITIAL_VENDOR_DATA.pulumi);
      }
    }
  };

  const newStepData = watch();

  const localFormData = useMemo(
    () => ({ ...formData, [currentStep]: newStepData }),
    [formData, currentStep, newStepData]
  );

  const processStepData = () => {
    return updateStepData(currentStep, newStepData);
  };

  const isDataChanged = !isEqual(newStepData, stepData);

  const validateForm = useCallback(async () => {
    return runFormValidations();
  }, [runFormValidations]);

  if (loading && networkStatus !== NetworkStatus.refetch) {
    return <FormLoading />;
  }

  const reloadLoading = loading && networkStatus === NetworkStatus.refetch;

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Choose vendor"
          description="Choose your infrastructure tool and configure its options"
        />

        <FormProvider {...builderForm}>
          <Controller
            name="vendor"
            control={control}
            render={({ field }) => (
              <Box direction="column" gap="x-large" margin="0 0 large">
                <TileCheckboxGrid>
                  {vendorTiles.map(({ name, logos, type, isNew }) => (
                    <Tile
                      key={type}
                      selected={field.value === type}
                      title={
                        isNew ? (
                          <Box gap="small" align="center">
                            <TileTitle>{name}</TileTitle>
                            <Badge type="weak" variant="blue" text="New" />
                          </Box>
                        ) : (
                          name
                        )
                      }
                      icon={
                        <Box gap="small">
                          {logos.map((logo, index) => (
                            <IconEmphasized
                              key={index}
                              icon={logo}
                              color={field.value === type ? "selected" : "gray"}
                            />
                          ))}
                        </Box>
                      }
                      onClick={() => {
                        if (type !== field.value) {
                          field.onChange(type);
                          resetVendorValues(type);
                          runFormValidations();
                        }
                      }}
                      indicator="radio"
                    />
                  ))}
                </TileCheckboxGrid>
                {field.value === StackCreationVendor.Terraform && (
                  <NewStackVendorTerraform
                    reloadVersionsData={refetch}
                    terraformVersions={data?.terraformVersions}
                    openTofuVersions={data?.openTofuVersions}
                    reloadLoading={reloadLoading}
                  />
                )}
                {field.value === StackCreationVendor.OpenTofu && (
                  <NewStackVendorOpenTofu
                    reloadVersionsData={refetch}
                    openTofuVersions={data?.openTofuVersions}
                    reloadLoading={reloadLoading}
                  />
                )}
                {field.value === StackCreationVendor.Pulumi && <NewStackVendorPulumi />}
                {field.value === StackCreationVendor.CloudFormation && (
                  <NewStackVendorCloudFormation />
                )}
                {field.value === StackCreationVendor.Kubernetes && (
                  <NewStackVendorKubernetes
                    reloadVersionsData={refetch}
                    kubectlVersions={data?.kubectlVersions}
                    reloadLoading={reloadLoading}
                  />
                )}
                {field.value === StackCreationVendor.Ansible && <NewStackVendorAnsible />}
                {field.value === StackCreationVendor.Terragrunt && (
                  <NewStackVendorTerragrunt
                    reloadVersionsData={refetch}
                    terraformVersions={data?.terraformVersions}
                    openTofuVersions={data?.openTofuVersions}
                    terragruntVersions={data?.terragruntVersions}
                    reloadLoading={reloadLoading}
                  />
                )}
              </Box>
            )}
          />
        </FormProvider>
      </FullScreenModalBody>

      <NewStackVendorDocumentation vendor={newStepData.vendor}>
        {({ body, link }) => (
          <NewStackFooter
            isDataChanged={isDataChanged}
            loading={asyncValidationLoading}
            processStepData={processStepData}
            documentationLink={link}
            documentationTitle="Choose vendor"
            documentationBody={body}
            analyticsDocsLocation={newStepData.vendor}
            // TODO: consider different approach
            localFormData={localFormData}
            runFormValidations={validateForm}
          />
        )}
      </NewStackVendorDocumentation>
    </>
  );
};

export default NewStackVendor;
